import { Fade } from "@/components/Reveal";
import { GhostCTA } from "@/components/CTA";

// Kicker al mount (non legato allo scroll): la pagina è sempre visibile immediatamente.
const Kicker = ({ index = "—", label, dark = false }) => (
  <Fade
    className={`mb-10 flex items-center gap-4 font-mono text-[11px] uppercase tracking-[0.3em] ${
      dark ? "text-white/45" : "text-ink/45"
    }`}
  >
    <span className={dark ? "text-accent" : "text-accent-dark"}>{index}</span>
    <span className={`h-px w-10 ${dark ? "bg-white/20" : "bg-ink/20"}`} aria-hidden="true" />
    <span>{label}</span>
  </Fade>
);

// Per aggiungere un evento futuro: aggiungi un oggetto a questo elenco.
// Campi: data, titolo, luogo, ruolo, descrizione, ctaLabel, ctaHref
const UPCOMING = [];

// Fotografie reali già presenti negli asset del progetto.
const GALLERY = [
  { src: "/assets/palco.jpg", alt: "Mattia Cosimo Saracino sul palco durante un evento di formazione" },
  { src: "/assets/gruppo-evento.jpg", alt: "Mattia Cosimo Saracino con i partecipanti a un evento di formazione" },
  { src: "/assets/duo-osm.jpg", alt: "Mattia Cosimo Saracino durante un incontro formativo" },
  { src: "/assets/osm-mare.jpg", alt: "Mattia Cosimo Saracino in un momento di confronto e formazione" },
];

const EventCard = ({ e }) => (
  <article
    data-testid={`event-card-${e.titolo.toLowerCase().replace(/\s/g, "-")}`}
    className="gold-glow-hover border border-white/10 bg-[#0C0C0C] p-8 lg:p-10"
  >
    <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-accent">{e.data}</p>
    <h3 className="mt-4 font-display text-2xl font-bold tracking-tight">{e.titolo}</h3>
    <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.22em] text-white/45">
      {e.luogo} — {e.ruolo}
    </p>
    <p className="mt-4 max-w-xl text-sm leading-relaxed text-white/55">{e.descrizione}</p>
    {e.ctaHref && (
      <GhostCTA to={e.ctaHref} dark testid="event-cta" className="mt-8">
        {e.ctaLabel || "Scopri l'evento"}
      </GhostCTA>
    )}
  </article>
);

const EventsPage = () => (
  <div data-testid="events-page" className="bg-ink pt-[72px] text-white">
    <section className="relative overflow-hidden">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_80%_20%,rgba(212,175,55,0.08),transparent_55%)]"
      />
      <div className="relative mx-auto max-w-[1440px] px-6 py-20 lg:px-12 lg:py-28">
        <Kicker index="—" label="Eventi & Formazione" dark />
        <Fade>
          <h1 className="font-display text-5xl font-extrabold tracking-tighter sm:text-6xl lg:text-7xl">
            EVENTI &<br />
            FORMAZIONE<span className="text-accent">.</span>
          </h1>
          <p className="mt-8 max-w-xl text-base leading-relaxed text-white/60 md:text-lg">
            Esperienze, incontri e momenti di formazione dedicati a imprenditori e aziende.
          </p>
        </Fade>
      </div>
    </section>

    <section data-testid="upcoming-events" className="border-t border-white/10">
      <div className="mx-auto max-w-[1440px] px-6 py-20 lg:px-12 lg:py-28">
        <Kicker index="01" label="In programma" dark />
        <Fade>
          <h2 className="font-display text-3xl font-extrabold tracking-tighter sm:text-4xl lg:text-5xl">
            Prossimi eventi
          </h2>
        </Fade>
        {UPCOMING.length > 0 ? (
          <div className="mt-12 grid gap-5 lg:grid-cols-2">
            {UPCOMING.map((e, i) => (
              <Fade key={e.titolo} delay={i * 0.08}>
                <EventCard e={e} />
              </Fade>
            ))}
          </div>
        ) : (
          <Fade delay={0.1} className="mt-12">
            <div className="flex max-w-2xl flex-col items-start gap-8 border border-white/10 bg-[#0C0C0C] p-8 lg:p-10">
              <p className="font-display text-2xl font-bold tracking-tight text-white/85">
                Nuovi appuntamenti in arrivo.
              </p>
              <p className="text-sm leading-relaxed text-white/50">
                Gli eventi in programma verranno pubblicati qui. Per speech, workshop o formazione
                aziendale puoi contattarmi direttamente.
              </p>
              <GhostCTA to="/contatti" dark testid="events-info-cta">
                Richiedi informazioni
              </GhostCTA>
            </div>
          </Fade>
        )}
      </div>
    </section>

    <section data-testid="events-gallery" className="border-t border-white/10">
      <div className="mx-auto max-w-[1440px] px-6 py-20 lg:px-12 lg:py-28">
        <Kicker index="02" label="Sul campo" dark />
        <Fade>
          <h2 className="font-display text-3xl font-extrabold tracking-tighter sm:text-4xl lg:text-5xl">
            Eventi e formazione.
          </h2>
        </Fade>
        <div className="mt-12 columns-2 gap-4 lg:columns-3 lg:gap-5">
          {GALLERY.map((p, i) => (
            <Fade key={p.src} delay={(i % 3) * 0.08} className="mb-4 break-inside-avoid lg:mb-5">
              <div className="group overflow-hidden border border-white/10">
                <img
                  src={p.src}
                  alt={p.alt}
                  loading="lazy"
                  data-testid={`events-photo-${i + 1}`}
                  className="h-auto w-full object-top transition-transform duration-700 group-hover:scale-[1.02]"
                />
              </div>
            </Fade>
          ))}
        </div>
      </div>
    </section>
  </div>
);

export default EventsPage;
